import React, {useEffect, useRef, useState} from "react";
import {useTranslate} from "@tolgee/react";
import {OutlinedInput} from "@material-ui/core";
import useStyles from "./styles";

type Props = {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  onComplete?: (value: string, autoSubmitted: boolean) => void;
};

const toSlots = (value: string, length: number): string[] => {
  const slots = value.split("").slice(0, length);
  while (slots.length < length) slots.push("");
  return slots;
};

const CodeInput: React.FC<Props> = ({
  value,
  onChange,
  length = 6,
  disabled,
  autoFocus,
  onComplete,
}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const slotKeys = useRef<string[]>(
    Array.from({length}, (_, index) => `code-slot-${index}`)
  );
  const [slots, setSlots] = useState<string[]>(() => toSlots(value, length));

  useEffect(() => {
    setSlots((prev) =>
      prev.join("") === value ? prev : toSlots(value, length)
    );
  }, [value, length]);

  const focusAt = (index: number) => {
    inputs.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  };

  const emit = (next: string[]) => {
    setSlots(next);
    const joined = next.join("");
    onChange(joined);
    if (joined.length === length) onComplete?.(joined, true);
  };

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const next = [...slots];
    next[index] = digit;
    emit(next);
    if (digit && index < length - 1) focusAt(index + 1);
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter") {
      const joined = slots.join("");
      if (joined.length === length) onComplete?.(joined, false);
      return;
    }
    if (event.key !== "Backspace" || slots[index]) return;
    if (index === 0) return;
    const next = [...slots];
    next[index - 1] = "";
    emit(next);
    focusAt(index - 1);
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);
    if (!pasted) return;
    emit(toSlots(pasted, length));
    focusAt(pasted.length - 1);
  };

  return (
    <div className={classes.root}>
      {slots.map((slot, index) => (
        <OutlinedInput
          key={slotKeys.current[index]}
          className={classes.box}
          classes={{
            input: classes.input,
            notchedOutline: classes.notchedOutline,
          }}
          value={slot}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          inputProps={{
            inputMode: "numeric",
            maxLength: 1,
            "aria-label": t("common.two_factor.code_input.digit_aria", {
              index: index + 1,
            }),
          }}
          inputRef={(el: HTMLInputElement | null) => {
            inputs.current[index] = el;
          }}
          onFocus={(event) => event.target.select()}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
};

export default CodeInput;
